import { api } from './api';
import type { Pagination } from './group.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface TeamMember {
  id: string;
  user: {
    id: string;
    full_name: string;
    email: string;
    avatar_url: string | null;
  };
  role: string;
  created_at: string;
}

export interface Team {
  id: string;
  reference_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  owner_id: string;
  workspace_id: string;
  created_at: string;
  updated_at: string;
  owner?: { id: string; full_name: string; avatar_url: string | null };
  members_count?: number;
  task_count?: number;
}

export interface TeamDetail extends Team {
  owner: { id: string; full_name: string; email: string; avatar_url: string | null };
  members: TeamMember[];
  current_user_role: string | null;
  current_user_permissions: {
    can_edit: boolean;
    can_delete: boolean;
    can_manage_members: boolean;
    can_change_roles: boolean;
    can_create_tasks: boolean;
    can_edit_any_task: boolean;
  };
}

export interface TeamListParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

export interface CreateTeamData {
  name: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const teamService = {
  list: (params?: TeamListParams) =>
    api.get<{ success: boolean; data: Team[]; pagination: Pagination }>('/teams', { params }),

  get: (id: string) =>
    api.get<{ success: boolean; data: TeamDetail }>(`/teams/${id}`),

  create: (data: CreateTeamData) =>
    api.post<{ success: boolean; data: Team }>('/teams', data),

  update: (id: string, data: Partial<Pick<Team, 'name' | 'description' | 'is_active'>>) =>
    api.patch<{ success: boolean; data: Team }>(`/teams/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/teams/${id}`),

  // Members
  listMembers: (id: string) =>
    api.get<{ success: boolean; data: TeamMember[]; pagination: Pagination }>(`/teams/${id}/members`),

  addMember: (id: string, data: { user_id: string; role?: string }) =>
    api.post<{ success: boolean; data: TeamMember }>(`/teams/${id}/members`, data),

  updateMemberRole: (id: string, memberId: string, role: string) =>
    api.patch<{ success: boolean; data: TeamMember }>(`/teams/${id}/members/${memberId}`, { role }),

  removeMember: (id: string, memberId: string) =>
    api.delete(`/teams/${id}/members/${memberId}`),
};
